// ManageUserSessionModal.tsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaMobileAlt, FaLaptop, FaSignOutAlt } from "react-icons/fa";
import styled from "styled-components";

interface Session {
  org_uid: string;
  user_uid: string;
  session_uid: string;
  created_at: string;
  expiry_datetime: string;
  last_active: string;
  session_status: string;
  session_timeout: string;
  session_type: string;
  device_info: {
    device_type: string;
    device_name?: string;
    model?: string;
    manufacturer?: string;
    platform?: string;
    version_code?: string | number;
    unique_id?: string;
  };
}

interface ManageUserSessionModalProps {
  targetUser: string;
  onClose: () => void;
  userUid: string;
  sessionUid: string;
  org_uid: string;
  beAddress?: string;
}

/* Styled components (same as before) */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 20px;
  width: 600px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 24px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
`;

const ScrollArea = styled.div`
  overflow-y: auto;
  padding-right: 12px;
`;

const DeviceCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  background: #f9f9f9;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
`;

const DeviceName = styled.div`
  font-weight: bold;
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 4px;
`;

const Info = styled.div`
  font-size: 0.9rem;
  color: #555;
  margin-bottom: 2px;
`;

const ExtraInfo = styled.div`
  margin-top: 8px;
  font-size: 0.9rem;
  color: #444;
  background: #eef5ff;
  padding: 12px;
  border-radius: 12px;
`;

const LogoutButton = styled.button`
  background: #d32f2f;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 12px;
  cursor: pointer;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;

  &:hover {
    background: #b71c1c;
  }
`;

const ManageUserSessionModal: React.FC<ManageUserSessionModalProps> = ({
  targetUser,
  onClose,
  userUid,
  sessionUid,
  org_uid,
  beAddress,
}) => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingLogoutId, setLoadingLogoutId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState("");

  const baseUrl =
    beAddress ||
    window._env_?.REACT_APP_API_URL ||
    process.env.REACT_APP_API_URL;

  const fetchUserSessions = async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const response = await axios.get(`${baseUrl}/api/user-active-sessions`, {
        headers: {
          "Content-Type": "application/json",
          "user-uid": userUid,
          "session-uid": sessionUid,
        },
        params: {
          org_uid,
          target_user: targetUser,
        },
      });

      if (response.data.status === "Success") {
        const userSessionMap = response.data.data?.user_session_map || {};
        const userSessions = userSessionMap[targetUser] || [];

        setSessions(userSessions);
      } else {
        setErrorMessage(response.data.message || "Failed to fetch sessions");
      }
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        setErrorMessage(
          err.response?.data?.message || "Failed to fetch sessions"
        );
      } else if (err instanceof Error) {
        setErrorMessage(err.message);
      } else {
        setErrorMessage("Unknown error occurred");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserSessions();
  }, []);

  const handleLogout = async (session: Session) => {
    if (!session.session_uid) return;

    setLoadingLogoutId(session.session_uid);

    try {
      await axios.delete(`${baseUrl}/api/user-active-sessions`, {
        headers: {
          "Content-Type": "application/json",
          "user-uid": userUid,
          "session-uid": sessionUid,
        },
        data: {
          org_uid: session.org_uid,
          user_uid: session.user_uid || targetUser,
          session_uid: session.session_uid,
        },
      });

      await fetchUserSessions();
    } catch (err) {
      console.error("Logout failed:", err);
      alert("Failed to logout session");
    } finally {
      setLoadingLogoutId(null);
    }
  };

  return (
    <ModalOverlay>
      <ModalBox>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <h3 style={{ margin: 0 }}>View Session Details</h3>
          <button
            onClick={onClose}
            style={{
              background: "#e0e0e0",
              border: "none",
              borderRadius: "50%",
              width: 32,
              height: 32,
              cursor: "pointer",
            }}
          >
            ✖
          </button>
        </div>

        <ScrollArea>
          {loading ? (
            <div>Loading sessions...</div>
          ) : errorMessage ? (
            <div style={{ color: "red" }}>{errorMessage}</div>
          ) : sessions.length === 0 ? (
            <div>No active sessions</div>
          ) : (
            sessions.map((session) => {
              const device = session.device_info;
              const Icon =
                device.device_type === "mobile" ? FaMobileAlt : FaLaptop;

              return (
                <DeviceCard key={session.session_uid}>
                  <Icon size={28} color="#1976d2" />
                  <div style={{ flex: 1 }}>
                    <DeviceName>
                      {device.device_name || "(Unnamed Device)"}
                    </DeviceName>
                    <Info>Platform: {device.platform || "-"}</Info>
                    <Info>Model: {device.model || "-"}</Info>
                    <Info>
                      Last Active:{" "}
                      {session.last_active
                        ? new Date(session.last_active).toLocaleString()
                        : "Never logged in"}
                    </Info>

                    <ExtraInfo>
                      <div>Session UID: {session.session_uid}</div>
                      <div>
                        Created At:{" "}
                        {new Date(session.created_at).toLocaleString()}
                      </div>
                      <div>
                        Last Active:{" "}
                        {new Date(session.last_active).toLocaleString()}
                      </div>
                      <div>
                        Expiry:{" "}
                        {new Date(session.expiry_datetime).toLocaleString()}
                      </div>

                      <LogoutButton
                        onClick={() => handleLogout(session)}
                        disabled={loadingLogoutId === session.session_uid}
                      >
                        <FaSignOutAlt />
                        {loadingLogoutId === session.session_uid
                          ? "Logging out..."
                          : "Logout"}
                      </LogoutButton>
                    </ExtraInfo>
                  </div>
                </DeviceCard>
              );
            })
          )}
        </ScrollArea>
      </ModalBox>
    </ModalOverlay>
  );
};

export default ManageUserSessionModal;
